import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { Button, MenuList, MenuItem, Typography, Popover } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ArrowBackIosSharpIcon from "@material-ui/icons/ArrowBackIosSharp";

import PermissionedDisplay from "../Layout/PermissionedDisplay";
import Search from "../FormElements/Search";

const useStyles = makeStyles((theme) => ({
  navItem: {
    width: "100%",
    height: theme.spacing(theme.navSpacingSize),
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 0,
    fontSize: "1.3rem",
    fontWeight: 500,
  },
  selectedButton: {
    boxShadow: `inset ${theme.spacing(1)}px 0 ${theme.palette.primary.red}`,
  },
  selectedMenu: {
    borderLeft: `${theme.spacing(1)}px solid white`,
  },
  textPrimary: {
    color: theme.palette.text.primary,
  },
  textSecondary: {
    color: theme.palette.text.secondary,
  },
}));

const VerticalNavItem = ({
  children,
  background,
  linkPath,
  menu,
  ariaLabel,
  selected,
  testId,
  autocomplete,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const history = useHistory();
  const theme = useTheme();
  const classes = useStyles(theme);
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [searchText, setSearchText] = useState("");

  const buttonClick = (e) => {
    if (!menu || !menu.length) {
      return history.push(linkPath);
    }
    setFilteredMenu(menu);
    setSearchText("");
    return setAnchorEl(e.currentTarget);
  };

  const menuItemClick = (link) => {
    setAnchorEl(null);
    history.push(link);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setFilteredMenu(menu);
  }, [menu]);

  useEffect(() => {
    setFilteredMenu(
      menu.filter(({ label }) => label.toLowerCase().startsWith(searchText.toLowerCase())),
    );
  }, [searchText]);

  return (
    <li>
      <Button
        className={clsx(classes.navItem, selected && classes.selectedButton)}
        style={{ background }}
        onClick={buttonClick}
        aria-label={ariaLabel}
        aria-haspopup={menu && Boolean(menu.length)}
        aria-expanded={Boolean(anchorEl)}
        aria-current={selected ? "page" : false}
        data-testid={testId}
      >
        {anchorEl ? <ArrowBackIosSharpIcon fontSize="large" /> : children}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "right", vertical: "top" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        style={{ maxHeight: "80%" }}
      >
        {autocomplete && <Search onChange={setSearchText} />}
        {filteredMenu && (
          <MenuList autoFocus={false} variant="menu" open onClose={handleClose}>
            {filteredMenu.map(({ label, path, isSelected, permissions, isSecondary }) => (
              <PermissionedDisplay permission={permissions} key={label}>
                <MenuItem
                  selected={isSelected}
                  className={clsx(isSelected && classes.selectedMenu)}
                  aria-current={isSelected ? "Page" : false}
                  onClick={() => menuItemClick(path)}
                >
                  <Typography
                    variant="h6"
                    className={isSecondary ? classes.secondary : classes.primary}
                  >
                    {label}
                  </Typography>
                </MenuItem>
              </PermissionedDisplay>
            ))}
          </MenuList>
        )}
      </Popover>
    </li>
  );
};

const MenuItemType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  isSelected: PropTypes.bool,
  permissions: PropTypes.array,
  isSecondary: PropTypes.bool,
});

MenuItemType.defaultProps = {
  isSelected: false,
  isSecondary: false,
  permissions: [],
};

VerticalNavItem.propTypes = {
  children: PropTypes.any.isRequired,
  background: PropTypes.string,
  linkPath: PropTypes.string,
  menu: PropTypes.arrayOf(MenuItemType),
  ariaLabel: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  testId: PropTypes.string,
  autocomplete: PropTypes.bool,
};

VerticalNavItem.defaultProps = {
  background: "None",
  linkPath: null,
  menu: [],
  selected: false,
  testId: null,
  autocomplete: false,
};

export default VerticalNavItem;
